import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { ApiKey, Backup, BaseModel, DatabaseHost, Domain, Egg, Location, Mod, Node, Schedule, ServerTemplate, User } from '~/api/models';
import type { Server } from '~/api/models/Server';

interface SetModelData<T = any> {
    name: keyof ModelsState;
    model: T;
    refresh: () => Promise<T>;
}

interface UpdateModelData<T = any> {
    name: keyof ModelsState;
    model: Partial<T>;
}

interface ModelsState {
    databaseHost?: DatabaseHost;
    server?: Server;
    location?: Location;
    node?: Node;
    mod?: Mod;
    egg?: Egg;
    user?: User;
    apiKey?: ApiKey;
    schedule?: Schedule;
    domain?: Domain;
    serverTemplate?: ServerTemplate;
    backup?: Backup;
}

type RefreshFunctions = Record<string, () => Promise<any>>;

export const useModelsStore = defineStore('models', () => {
    // State
    const models = ref<ModelsState>({});
    const refreshFunctions = ref<RefreshFunctions>({});

    // Getters
    const getModel = computed(() => <T extends BaseModel>(name: keyof ModelsState): T | undefined => {
        return models.value[name] as T | undefined;
    });

    const hasModel = computed(() => (name: keyof ModelsState): boolean => {
        return !!models.value[name];
    });

    const getAllModels = computed(() => models.value);

    const getModelNames = computed(() => Object.keys(models.value) as (keyof ModelsState)[]);

    const getRefreshFunction = computed(() => (name: keyof ModelsState) => {
        return refreshFunctions.value[name];
    });

    const hasRefreshFunction = computed(() => (name: keyof ModelsState): boolean => {
        return !!refreshFunctions.value[name];
    });

    // Specific model getters
    const databaseHost = computed(() => models.value.databaseHost);
    const server = computed(() => models.value.server);
    const location = computed(() => models.value.location);
    const node = computed(() => models.value.node);
    const mod = computed(() => models.value.mod);
    const egg = computed(() => models.value.egg);
    const user = computed(() => models.value.user);
    const apiKey = computed(() => models.value.apiKey);
    const schedule = computed(() => models.value.schedule);
    const domain = computed(() => models.value.domain);
    const serverTemplate = computed(() => models.value.serverTemplate);
    const backup = computed(() => models.value.backup);

    // Actions
    const setModel = <T = any>(data: SetModelData<T>) => {
        models.value[data.name] = data.model as any;
        refreshFunctions.value[data.name] = data.refresh;
    };

    const updateModel = <T = any>(data: UpdateModelData<T>) => {
        const model = models.value[data.name] as any;
        if (model && typeof model.update === 'function') {
            model.update(data.model);
        }
    };

    const clearModel = (name: keyof ModelsState) => {
        delete models.value[name];
        delete refreshFunctions.value[name];
    };

    const refreshModel = async (name: keyof ModelsState) => {
        const refreshFunc = refreshFunctions.value[name];

        if (typeof refreshFunc === 'function') {
            try {
                const refreshedModel = await refreshFunc();
                models.value[name] = refreshedModel;
                return refreshedModel;
            } catch (error) {
                console.error(`Failed to refresh model ${name}:`, error);
                throw error;
            }
        } else {
            throw new Error(`No refresh function available for model: ${name}`);
        }
    };

    const clearAllModels = () => {
        models.value = {};
        refreshFunctions.value = {};
    };

    // Batch operations
    const setMultipleModels = <T = any>(modelData: SetModelData<T>[]) => {
        modelData.forEach((data) => setModel(data));
    };

    const refreshAllModels = async () => {
        const refreshPromises = Object.keys(refreshFunctions.value).map((name) =>
            refreshModel(name as keyof ModelsState).catch((error) => {
                console.error(`Failed to refresh model ${name}:`, error);
                return null;
            }),
        );

        const results = await Promise.allSettled(refreshPromises);
        return results;
    };

    const getModelsWithRefreshFunctions = computed(() => {
        return (Object.keys(models.value) as (keyof ModelsState)[]).filter((name) => hasRefreshFunction.value(name));
    });

    // Model type helpers
    const isDatabaseHost = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'databaseHost' && hasModel.value(name);
    });

    const isServer = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'server' && hasModel.value(name);
    });

    const isLocation = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'location' && hasModel.value(name);
    });

    const isNode = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'node' && hasModel.value(name);
    });

    const isMod = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'mod' && hasModel.value(name);
    });

    const isEgg = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'egg' && hasModel.value(name);
    });

    const isUser = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'user' && hasModel.value(name);
    });

    const isApiKey = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'apiKey' && hasModel.value(name);
    });

    const isSchedule = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'schedule' && hasModel.value(name);
    });

    const isDomain = computed(() => (name: keyof ModelsState): boolean => {
        return name === 'domain' && hasModel.value(name);
    });

    return {
        // State
        models,
        refreshFunctions,

        // Getters
        getModel,
        hasModel,
        getAllModels,
        getModelNames,
        getRefreshFunction,
        hasRefreshFunction,

        // Specific model getters
        databaseHost,
        server,
        location,
        node,
        mod,
        egg,
        user,
        apiKey,
        schedule,
        domain,
        serverTemplate,
        backup,

        // Actions
        setModel,
        updateModel,
        clearModel,
        refreshModel,
        clearAllModels,

        // Batch operations
        setMultipleModels,
        refreshAllModels,
        getModelsWithRefreshFunctions,

        // Model type helpers
        isDatabaseHost,
        isServer,
        isLocation,
        isNode,
        isMod,
        isEgg,
        isUser,
        isApiKey,
        isSchedule,
        isDomain,
    };
});

// Export types for external use
export type ModelsStore = ReturnType<typeof useModelsStore>;
export type { ModelsState, SetModelData, UpdateModelData };
